import React from "react";
import { motion } from "framer-motion";

/**
 * Hero-level visual of 3-way reconciliation.
 * Three ledger columns (Bank, Book, Client) connected via SVG lines
 * that converge into a single "MATCH" node.
 */
export default function ThreeWayReconciliation() {
  const columns = [
    {
      id: "bank",
      label: "Bank Balance",
      sub: "Operating + IOLTA account statements",
      color: "#0C1D36",
      rows: [
        { k: "Operating", v: "$412,830.12" },
        { k: "IOLTA", v: "$1,204,508.00" },
        { k: "Cleared", v: "98.7%" },
      ],
    },
    {
      id: "book",
      label: "Book Balance",
      sub: "General ledger per QuickBooks / Xero",
      color: "#BA1A1A",
      rows: [
        { k: "GL Operating", v: "$412,830.12" },
        { k: "GL Trust", v: "$1,204,508.00" },
        { k: "Posted", v: "100%" },
      ],
    },
    {
      id: "client",
      label: "Client Ledgers",
      sub: "Sum of every individual client balance",
      color: "#0C1D36",
      rows: [
        { k: "58 Active Matters", v: "" },
        { k: "Σ client funds", v: "$1,204,508.00" },
        { k: "Unreconciled", v: "$0.00" },
      ],
    },
  ];

  return (
    <div className="relative" data-testid="three-way-reconciliation">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 relative z-10">
        {columns.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="bg-white border border-[#0C1D36]/12 p-6 md:p-7 relative"
            data-testid={`3way-col-${c.id}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-serif text-2xl text-[#0C1D36] leading-tight">
                  {c.label}
                </h3>
                <p className="text-xs text-[#4B5563] mt-2 max-w-[22ch]">{c.sub}</p>
              </div>
              <span
                className="mt-1 w-2 h-2 rounded-full"
                style={{ backgroundColor: c.color }}
              />
            </div>

            <div className="mt-6 divide-y divide-[#0C1D36]/10 font-mono text-[13px]">
              {c.rows.map((r) => (
                <div key={r.k} className="flex items-center justify-between py-2.5">
                  <span className="text-[#4B5563]">{r.k}</span>
                  <span className="text-[#0C1D36]">{r.v}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#0C1D36]/70">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
              Verified
            </div>
          </motion.div>
        ))}
      </div>

      {/* connecting lines */}
      <svg
        className="hidden md:block absolute inset-0 w-full h-full pointer-events-none"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        <motion.path
          d="M 16 78 C 30 95, 50 95, 50 95"
          stroke="#BA1A1A"
          strokeWidth="0.3"
          fill="none"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, delay: 0.3 }}
        />
        <motion.path
          d="M 50 78 L 50 95"
          stroke="#BA1A1A"
          strokeWidth="0.3"
          fill="none"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, delay: 0.5 }}
        />
        <motion.path
          d="M 84 78 C 70 95, 50 95, 50 95"
          stroke="#BA1A1A"
          strokeWidth="0.3"
          fill="none"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, delay: 0.7 }}
        />
      </svg>

      {/* Match node */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 1.1 }}
        className="mt-10 md:mt-14 flex justify-center"
      >
        <div className="bg-[#0C1D36] text-[#FDFBF7] px-10 py-6 text-center relative">
          <div className="eyebrow-muted text-white/60 mb-2">All Three Must Match</div>
          <div className="font-serif text-3xl md:text-4xl">
            Reconciled <span className="text-white">·</span> Audit-Ready
          </div>
          <div className="text-xs text-white/60 mt-2 font-mono">
            bank = book = Σ(client ledgers)
          </div>
          <span className="absolute -top-px left-0 right-0 h-px bg-[#BA1A1A]" />
        </div>
      </motion.div>
    </div>
  );
}
